import {
  Ajax, Avatar, Calculation, CalculationCollection, CalculationResultLogic, Calculations, CalculationStatus, CalculatorLogic,
  Dates, InputEnumOption, ExternalDialogData, Invoice, InvoicePreview, Invoices, InvoiceStatus, InvoicesLogic, InvoiceType, LegalEntityType, Numeric,
  PaymentChannel, PayrollDetails, PayrollStatus, Translations, SalaryDateLogic, InvoiceListItem, SalaryDateKind, Payrolls4, Configs
} from "@salaxy/core";
import angular from "angular";

import { SessionService } from "./SessionService";
import { SettingsService } from "./SettingsService";
import { OnboardingService } from "./OnboardingService";
import { PayrollLogic } from "../controllers/calc/PayrollLogic";
import { EditDialogKnownActions, UiHelpers } from "./ui";

/** Implements the user interface logic for new Invoices: Salary and side-cost payments. */
export class InvoicesService {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Invoices", "SessionService", "UiHelpers", "SettingsService", "AjaxNg1", "$location", "$timeout", "Calculations", "OnboardingService"];

  private _defaultSepaChannel: PaymentChannel = PaymentChannel.PalkkausManual;

  private _defaultPaymentListChannel: PaymentChannel = PaymentChannel.PalkkausWS;

  constructor(
    private invoicesApi: Invoices,
    private sessionService: SessionService,
    private uiHelpers: UiHelpers,
    private settingsService: SettingsService,
    private ajax: Ajax,
    private $location: angular.ILocationService,
    private $timeout: angular.ITimeoutService,
    private calculations: Calculations,
    private onboardingService: OnboardingService,

  ) {
  }

  /** Gets the default SEPA payment channel. */
  public get defaultSepaChannel(): PaymentChannel {
    return this._defaultSepaChannel;
  }

  public set defaultSepaChannel(value: PaymentChannel) {
    this._defaultSepaChannel = value;
  }

  /** Gets the default payment channel. */
  public get defaultPaymentListChannel(): PaymentChannel {
    return this._defaultPaymentListChannel;
  }

  public set defaultPaymentListChannel(value: PaymentChannel) {
    this._defaultPaymentListChannel = value;
  }

  /**
   * Application level setting of the default payment channel.
   */
  public get defaultChannel(): PaymentChannel {
    if (this.channels) {
      const defaultChannel = this.sessionService.getSession().settings.defaultPaymentChannel;
      const confirmedChannel = this.channels.filter(x => x.id === defaultChannel).concat(this.channels).find(() => true)?.id as PaymentChannel;
      return confirmedChannel;
    }
    return null;
  }

  /**
   * Gets the enabled payment channels as avatars for the current session.
   */
  public get channels(): Avatar[] {
    const channelsForRole = () => this.sessionService.getSession().settings?.paymentChannels.filter((x) =>
      x !== PaymentChannel.PalkkausManual ||
      (x === PaymentChannel.PalkkausManual && this.sessionService.isInSomeRole("pro,sepaPartner"))
    );
    return this.uiHelpers.cache(this, "channels",
      () => channelsForRole()?.map((x) => InvoicesLogic.getChannelAvatar(x))?.filter(x => !!x),
      () => channelsForRole());
  }

  /**
   * Gets the enabled payment channels as input enum options for the current session.
   */
  public get channelEnumOptions(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "channelEnumOptions",
      () => this.channels?.map((x) => ({ text: x.displayName, value: x.id, title: x.description })),
      () => this.sessionService.getSession()?.settings.paymentChannels);
  }

  /**
   * Shows the paymment page as dialog.
   * @param paymentObject - The object to pay: Either Calculation or Payroll
   */
  public showPaymentDialog(paymentObject: Calculation | PayrollDetails) {
    if (!this.checkAccountVerificationForPayment()) {
        this.showMissingSignatureAlert();
        return;
    }

    if (!this.checkChangedSalaryDate(paymentObject)) {
      this.showChangedSalaryDateAlert();
      return;
    }

    let channel = null;
    if (InvoicesLogic.isPayroll(paymentObject)) {
      channel = paymentObject.input.paymentChannel;
     } else if (InvoicesLogic.isCalculation(paymentObject)) {
      if (this.sessionService.isInRole("person")) {
        paymentObject.info.paymentChannel = PaymentChannel.PalkkausPersonal;
      }
      channel = paymentObject.info.paymentChannel;
    }

    if(InvoicesLogic.isEcfa(channel)) {
      if(!this.checkKycForPayment()) {
        this.showMissingKycAlert();
        return;
      }
    }

    const logic = {
      title:
        InvoicesLogic.isPalkkausInvoicesLike(channel) ? Translations.get("SALAXY.NG1.InvoicesService.logic.title.palkkausInvoices") :
        Translations.get("SALAXY.NG1.InvoicesService.logic.title.default"),
      type: InvoicesLogic.isPayroll(paymentObject) ? "payroll" : "calc",
      okButtonText:
        InvoicesLogic.isPalkkausInvoicesLike(channel) ? Translations.get("SALAXY.NG1.InvoicesService.logic.okButtonText.palkkausInvoices") :
        InvoicesLogic.isCfa(channel) ? Translations.get("SALAXY.NG1.InvoicesService.logic.okButtonText.isCfaChannel") : Translations.get("SALAXY.NG1.InvoicesService.logic.okButtonText.default"),
      channel,
      paymentFunc: null,
      isNullPayment: () => ( InvoicesLogic.isCalculation(paymentObject) && Numeric.round(paymentObject.result.employerCalc.totalPayment) == 0 )
                       ||
                      ( InvoicesLogic.isPayroll(paymentObject) && Numeric.round(paymentObject.info.totalPayment) == 0),
      isReadyForPayment: () => InvoicesLogic.isPayroll(paymentObject) ?
      paymentObject.info &&
      paymentObject.info.isReadyForPayment :
        InvoicesLogic.isCalculation(paymentObject) ?
        paymentObject.worker &&
        paymentObject.worker.tax.isValid &&
          !paymentObject.result.validation.errors.find((e) => (e.type !== "warning")) :
          false,
      instructions: null
    };

    const saveForPreview = (): Promise<Calculation | PayrollDetails> => {
      if (InvoicesLogic.isCalculation(paymentObject) &&
          (
            channel == PaymentChannel.PalkkausPersonal ||
            channel == PaymentChannel.PalkkausEcfaPaytrail
          )) {
        if (!paymentObject.workflow?.status || paymentObject.workflow.status === CalculationStatus.Draft) {
          const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.InvoicesService.saving") + "...");
          return this.calculations.save(paymentObject).then((savedCalculation) => {
            loader.dismiss();
            angular.copy(savedCalculation, paymentObject);
            this.$location.path("/calc/details/" + paymentObject.id + "&refresh=" + new Date().getTime());
            return paymentObject;
          });
        }
        else
        {
          return Promise.resolve(paymentObject);
        }
      }
      else
      {
        return Promise.resolve(paymentObject);
      }
    };

    const paymentObjectForCfa = (): Promise<Calculation | PayrollDetails> => {
      if (InvoicesLogic.isPayroll(paymentObject)) {
        if (paymentObject.calcs.length > 0 ) {
          return Promise.resolve(paymentObject);
        } else {
          const payrolls4Api = new Payrolls4(this.ajax);
          return payrolls4Api.getCalcsOData(paymentObject.id, null).then((listItemResult) => {
            const calcs = listItemResult.items;
            const data: any = {};
            data.listItemType = "CalculationListItem";
            const columns = undefined;
            const paramsTemplate = undefined;
            const action = (items /*, data */) => {
              const itemIds = items.map((x) => x.id);
              return this.calculations.getMulti(itemIds).then((result) => {
                const results = items.map(() => ({ message: "", result: [] }));
                results[0].result = result;
                return results;
              });
            };
            return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.UI_Terms.wait"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
              const payrollCalcs = [];
              if (dialogResult.done) {
                dialogResult.results.forEach((result) => {
                  payrollCalcs.push(...result.result);
                });
              }
              const newPaymentObject:any = angular.copy(paymentObject);
              newPaymentObject.calcs = payrollCalcs;
              newPaymentObject["V4"] = true;

              return newPaymentObject;
            });
          });
        }
      } else {
        return Promise.resolve(paymentObject);
      }
    };

    if (InvoicesLogic.isCfa(channel) && channel !== PaymentChannel.PalkkausEcfaPaytrail) {
        paymentObjectForCfa().then((reportingPaymentObject) => {
          paymentObject = reportingPaymentObject;
          this.uiHelpers.openEditDialog("salaxy-components/modals/calc/CfaPaymentOverview.html", paymentObject, logic, "lg").then((result) => {
          if (result.action !== EditDialogKnownActions.Ok) {
            return;
          }
          this.createCfaInvoice(channel, paymentObject);
        });
      });
    } else {
      switch (channel) {
        case PaymentChannel.PalkkausEcfaPaytrail:
          {
            paymentObjectForCfa().then((reportingPaymentObject) => {
              paymentObject = reportingPaymentObject;
              logic.paymentFunc = (pObj) => this.createPalkkausCfaPaytrailInvoice(pObj);
              saveForPreview().then(() => this.uiHelpers.openEditDialog("salaxy-components/modals/calc/CfaPaymentOverview.html", paymentObject, logic, "lg"));
            });
          }
          break;
        case PaymentChannel.PalkkausWS: {
          this.uiHelpers.openEditDialog("salaxy-components/modals/calc/WSPaymentOverview.html", paymentObject, logic, "lg");
          break;
        }
        case PaymentChannel.PalkkausPersonal:
          {
            saveForPreview().then(() => {
              this.uiHelpers.openEditDialog("salaxy-components/modals/calc/PalkkausPersonalPaymentOverview.html", paymentObject, logic, "lg", "PalkkausPersonalPaymentOverviewController").then((result) => {
                if (result.action) {
                  if (result.action == InvoiceStatus.Canceled) {
                    this.createPalkkausPersonalInvoice(channel, paymentObject, InvoiceStatus.Canceled);
                    this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.headingCancelled"), `${Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.cancel.infotext1")}.\r\n${Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.cancel.infotext2")}.`
                    , Translations.get("SALAXY.UI_Terms.ok"));
                  } else if (result.action === InvoiceStatus.Paid) {
                    this.createPalkkausPersonalInvoice(channel, paymentObject, InvoiceStatus.Paid);
                    this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.headingPaid"), `${Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.paid.text")}\r\n${Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.paid.infoText1")}\r\n${Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.paid.infoText2")}\r\n${Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.alerts.paid.infoText3")}`
                    , Translations.get("SALAXY.UI_Terms.ok"));
                  }
                }
              });
            });
          }
          break;
        default:
          this.uiHelpers.openEditDialog("salaxy-components/modals/calc/PaymentOverview.html", paymentObject, logic, "lg");
          break;
      }
    }
  }

  /**
   * Shows the payment invoice inspection page as dialog.
   * @param paymentObject - Paid calculation
   */
  public showPalkkausPersonalInvoicesDialog(paymentObject: Calculation) {
    const logic = {isInspectingPaidInvoices: true};
    this.uiHelpers.openEditDialog("salaxy-components/modals/calc/PalkkausPersonalPaymentOverview.html", paymentObject, logic, "xl", "PalkkausPersonalPaymentOverviewController");
  }

  /**
   * Querys item and opens paymentdialog
   * @param item InvoiceListItem used to query calculation
   */
  public showPaymentDialogForCalculation(item: InvoiceListItem) {
    this.calculations.getSingle(item.businessObjects[0]).then(calculation => {
      this.showPaymentDialog(calculation);
    });
  }

  /**
   * Shows an alert if the signature is missing.
   */
   public showMissingSignatureAlert() {
    this.uiHelpers.showConfirm(Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.signatureMissing"),
    Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.creationCompleteBeforePay") + "\n" +
    Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.signSignature") + "\n\n" +
    Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.continueToSignature"),
    Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.continue"), Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.close")).then((ok: boolean) => {
        if (ok) {
          if (this.sessionService.getCurrentAccount().entityType === LegalEntityType.Person ) {
            this.onboardingService.launchHouseholdOnboarding();
          } else {
            this.onboardingService.launchCompanyOnboarding();
          }
        }
      });
  }


  /**
   * Checks if the company's KYC (Know Your Customer) is completed and not expired for payment.
   * @returns {boolean} Returns true if the KYC is completed and not expired, otherwise false.
   */
  public checkKycForPayment(): boolean {
    const kycExpiresAt = this.sessionService?.kycExpiresAt;
    if (!kycExpiresAt) {
      return false;
    }
    // Expire check valid after 1.9.2025
    //if (Dates.isBefore(Dates.getToday(), "2025-09-01")){
      return true;
    //}

    return Dates.isBefore(Dates.getToday(), kycExpiresAt);
  }

  /**
   * Shows an alert if the kyc is missing.
   */
  public showMissingKycAlert() {
    this.uiHelpers.showConfirm(
    "Asiakkaan tuntemistiedot puuttuvat",
    null,
    "Päivitä asiakkaan tuntemistiedot nyt",
    Translations.get("SALAXY.NG1.InvoicesService.missingSignatureAlert.close"),
    "Asiakasvaratilin kautta maksaminen edellyttää asiakkaan tuntemistietojen päivittämistä. Päivitä KYC-tiedot oheisesta linkistä tai lue ohjeet, kuinka voit välittää päivityslinkin yhtiön nimenkirjoitusoikeudelliselle henkilölle täältä: <a href=\"https://help.palkkaus.fi/hc/fi/articles/19590957330589-Asiakkaan-tuntemistietojen-päivitys-KYC\" target=\"_blank\">Asiakkaan tuntemistietojen päivittäminen</a>"
  ).then((ok: boolean) => {
        if (ok) {
          window.open(Configs.current.isTestData ? "https://rc-iam.palkkaus.fi/wizard/index.html" : "https://iam.palkkaus.fi/wizard/index.html", "_blank");
        }
      });
  }

  /**
   * Returns true if the current authenticated user has
   * required verification for the payment.
   */
   public checkAccountVerificationForPayment(): boolean {
    return this.sessionService.checkAccountVerification();
  }

  /** Returns warning message text for the invoice if reported late.*/
  public getEprWarningForPayment(salaryDate: string): string {
    const warning = this.isDelayed(salaryDate) ? Translations.get("SALAXY.NG1.InvoicesService.eprWarningForPayment.isDelayed") : "";
    return warning;
  }

  /** Returns warning message text for the invoice if reported late.*/
  public getEprWarningForCancellation(salaryDate: string, isPaid: boolean): string {
    if (!isPaid) {
      return null;
    }
    const warning = this.isDelayed(salaryDate) ? Translations.get("SALAXY.NG1.InvoicesService.eprWarningForCancellation.isDelayed") + "\n\n" + Translations.get("SALAXY.NG1.InvoicesService.eprWarningForCancellation.infoText") : "";
    return warning;
  }

 /**
  * Checks the salary date validity before payment.
  * @param paymentObject - The object to pay: Either Calculation or Payroll
  */
 public checkChangedSalaryDate(paymentObject: Calculation | PayrollDetails) {
  const role =  this.sessionService.isInRole("pro") ? "pro" :
                this.sessionService.isInRole("admin") ? "admin" : null;
  if (InvoicesLogic.isPayroll(paymentObject)) {
    if (paymentObject.info.status != PayrollStatus.Draft &&
        paymentObject.info.status != PayrollStatus.WaitingApproval)
    {
      return true;
    }
    if (!paymentObject.input.salaryDate) {
      return true;
    }
    if (paymentObject.input.salaryDate != paymentObject.info.salaryDate) {
      return false;
    }
    if (paymentObject.input.salaryDateKind == SalaryDateKind.InPast) {
      return true;
    }
    return SalaryDateLogic.isValidSalaryDate(paymentObject.info.salaryDate, null, null, !!paymentObject.input.salaryDate, paymentObject.input.paymentChannel, PayrollLogic.isZeroPayment(paymentObject.calcs), role);
  } else if (InvoicesLogic.isCalculation(paymentObject)) {
    if (this.sessionService.isInRole("person")) {
      paymentObject.info.paymentChannel = PaymentChannel.PalkkausPersonal;
    }
    if (paymentObject.workflow.status != CalculationStatus.Draft &&
        paymentObject.workflow.status != CalculationStatus.WaitingApproval &&
        paymentObject.workflow.status != CalculationStatus.ProDraft
      ) {
      return true;
    }
    if (!paymentObject.workflow.requestedSalaryDate) {
      return true;
    }
    if (paymentObject.workflow.requestedSalaryDate != paymentObject.workflow.salaryDate) {
      return false;
    }
    if (paymentObject.workflow.salaryDateKind == SalaryDateKind.InPast) {
      return true;
    }
    return SalaryDateLogic.isValidSalaryDate(paymentObject.workflow.salaryDate, paymentObject.worker?.paymentData?.ibanNumber, null, !!paymentObject.workflow.requestedSalaryDate, paymentObject.info.paymentChannel, CalculatorLogic.isZeroPayment(paymentObject), role);
  }
  return true;
 }

  /**
   * Shows an alert for invalid salary date.
   */
   public showChangedSalaryDateAlert() {
    this.uiHelpers.showAlert(Translations.get("SALAXY.VALIDATION.Calc.SalaryDateChanged.label"),Translations.get("SALAXY.VALIDATION.Calc.SalaryDateChanged.description") );
   }

  /** Checks if the Epr report is delayed. */
  private isDelayed(salaryDate?: string): boolean {
      const officialDate = Dates.add((salaryDate ?? "today"),5, "day");
      const expiresAt = Dates.addWorkdays(officialDate, 0);
      const today = Dates.asDate("today");
      return today > expiresAt;
}

  private createCfaInvoice(channel: PaymentChannel, paymentObject: Calculation | PayrollDetails) {

    const previewInvoices = (): Promise<InvoicePreview[]> => {
      const coll: CalculationCollection = {};
      if (InvoicesLogic.isPayroll(paymentObject)) {
        if ((paymentObject as any).V4) {
          const payrolls4Api = new Payrolls4(this.ajax);
          return payrolls4Api.previewInvoices(paymentObject.id, [], false);
        }
        else
        {
          coll.payrollIds = [paymentObject.id];
          return this.invoicesApi.previewInvoices(channel, coll);
        }
      } else {
        coll.calcs = [paymentObject];
        return this.invoicesApi.previewInvoices(channel, coll);
      }
    };

    const createNewInvoice = () => {
      if (InvoicesLogic.isPayroll(paymentObject)) {
        if ((paymentObject as any).V4) {
          const payrolls4Api = new Payrolls4(this.ajax);
          return payrolls4Api.createInvoices(paymentObject.id, [], false);
        }
        else
        {
          return this.invoicesApi.createInvoicesForPayroll(channel, paymentObject.id);
        }
      } else {
        return this.invoicesApi.createInvoices(paymentObject, channel);
      }
    };

    const createInvoice = (): Promise<Invoice> => {
      return previewInvoices().then((previews) => {
        const grossInvoicePreview = previews.find((x) => x.invoice.entityType == InvoiceType.Gross);
        if (!grossInvoicePreview) {
          return null;
        }
        if (grossInvoicePreview.exists) {
          return this.invoicesApi.getSingle(grossInvoicePreview.invoice.id);
        }
        return createNewInvoice().then((invoices) => {
          return invoices.find((x) => x.header.type == InvoiceType.Gross);
        });
      });
    };

    const navigateToPaymentObject = (businessObjectId) => {
      if (InvoicesLogic.isPayroll(paymentObject)) {
        this.$location.path("/payroll/details/" + businessObjectId + "&refresh=" + new Date().getTime());
      } else {
        this.$location.path("/calc/details/" + businessObjectId + "&refresh=" + new Date().getTime());
      }
    };

    const isPaid = (invoice: Invoice) => {
      if (invoice.header.status === InvoiceStatus.Paid) {
        this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.isPaid.payment"), Translations.get("SALAXY.NG1.InvoicesService.isPaid.paid"));
        return true;
      }
      else if (invoice.header.status !== InvoiceStatus.PaymentStarted && invoice.header.status !== InvoiceStatus.Unread)
      {
        this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.isPaid.cantCreatePayment"), Translations.get("SALAXY.NG1.InvoicesService.isPaid.cantCreatePaymentAlreadyPaid"));
        return true;
      }
      return false;
    };

    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.InvoicesService.creatingPayment")+ "...");
    createInvoice().then((grossInvoice) => {
      if (!grossInvoice) {
        loader.dismiss();
        this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.createInvoice.cantCreateInvoice"), Translations.get("SALAXY.NG1.InvoicesService.createInvoice.cantCreateInvoiceCheck"));
        return;
      }
      switch (channel) {
        case PaymentChannel.PalkkausEcfaFinvoice:
          {
            if (isPaid(grossInvoice)) {
              loader.dismiss();
              navigateToPaymentObject(grossInvoice.header.businessObjects[0]);
            } else {
              loader.dismiss();
              const settingsReceiver = this.settingsService.current.payments.invoice.eInvoiceReceiver;
              const settingsIntermediator = this.settingsService.current.payments.invoice.eInvoiceIntermediator;
              const item = {
                eInvoiceReceiver: settingsReceiver,
                eInvoiceIntermediator: settingsIntermediator,
              };
              const showAddressSelection = !settingsReceiver || !settingsIntermediator;

              const sendEInvoice = (address: { eInvoiceReceiver: string, eInvoiceIntermediator: string }): Promise<any> => {
                const sendLoader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.InvoicesService.sending")+ "...");
                if (address.eInvoiceReceiver !== settingsReceiver || address.eInvoiceIntermediator !== settingsIntermediator) {
                  const settings = this.settingsService.current;
                  settings.payments.invoice.eInvoiceReceiver = address.eInvoiceReceiver;
                  settings.payments.invoice.eInvoiceIntermediator = address.eInvoiceIntermediator;
                  this.settingsService.save();
                }

                return this.invoicesApi.sendEInvoice(grossInvoice.id, address.eInvoiceReceiver, address.eInvoiceIntermediator).then((result) => {
                  sendLoader.dismiss();
                  let title = Translations.get("SALAXY.NG1.InvoicesService.sendEInvoice.ajax.title");
                  let msg = `${Translations.get("SALAXY.NG1.InvoicesService.sendEInvoice.ajax.msg")} ${address.eInvoiceIntermediator}/${address.eInvoiceReceiver}.`;
                  let freeText = "";
                  if (result.Response.Status !== "OK") {
                    title = Translations.get("SALAXY.NG1.InvoicesService.sendEInvoice.ajax.error.title");
                    msg = Translations.get("SALAXY.NG1.InvoicesService.sendEInvoice.ajax.error.msg");
                    if (result.Response.FreeText) {
                      for (const ft of result.Response.FreeText) {
                        freeText += ft["#text"] + "\n";
                      }
                    }
                  }
                  freeText = this.textToRows(freeText, 80, "\n");
                  this.uiHelpers.showAlert(title, msg + "\n" + Translations.get("SALAXY.NG1.InvoicesService.sendEInvoice.ajax.operatorStatus") + ":" + "\n" + result.Response.Status + " " + result.Response.StatusCode + "\n" + freeText);
                  return result;
                });
              };

              if (showAddressSelection) {
                const eInvoiceLogic = {
                  selectEInvoiceAddress: (itemRef) => {
                    this.settingsService.selectEInvoiceAddress(itemRef);
                  },
                };

                this.uiHelpers.openEditDialog("salaxy-components/modals/payment/cfa/EInvoice.html", item, eInvoiceLogic, null).then((result) => {
                  if (result.action !== EditDialogKnownActions.Ok) {
                    navigateToPaymentObject(grossInvoice.header.businessObjects[0]);
                  } else {
                    sendEInvoice(result.item).then(() => {
                      navigateToPaymentObject(grossInvoice.header.businessObjects[0]);
                    });
                  }
                });
              } else {
                sendEInvoice(item).then(() => {
                  navigateToPaymentObject(grossInvoice.header.businessObjects[0]);
                });
              }
            }
          }
          break;
        case PaymentChannel.PalkkausEcfaReference:
          {
            loader.dismiss();

            const cfaReferenceCalculations = [];
            if (InvoicesLogic.isPayroll(paymentObject)) {
              cfaReferenceCalculations.push(...paymentObject.calcs);
            } else if (InvoicesLogic.isCalculation(paymentObject)) {
              cfaReferenceCalculations.push(paymentObject);
            }

            const cfaReferenceTotalResultCalculation = CalculatorLogic.getBlank();
            cfaReferenceTotalResultCalculation.worker.tax = cfaReferenceTotalResultCalculation.worker.tax || {};
            cfaReferenceTotalResultCalculation.result = CalculationResultLogic.add(cfaReferenceCalculations.map((x) => x.result));

            const referenceLogic = {
              printClass: "sxyFullPrint",
              print: (pcls: "sxyFullPrint" | "sxySecurePrint") => {
                referenceLogic.printClass = pcls;
                setTimeout(() => {
                  window.print();
                }, 0);
              },
              calculations: cfaReferenceCalculations,
              totalResultCalculation: cfaReferenceTotalResultCalculation,
            };
            // show dialog for every invoice status, for paid too
            this.uiHelpers.openEditDialog("salaxy-components/modals/payment/cfa/Transfer.html", grossInvoice, referenceLogic, "xl").then(() => {
              navigateToPaymentObject(grossInvoice.header.businessObjects[0]);
            });
          }
          break;
      }
    });
  }

  private createPalkkausCfaPaytrailInvoice(paymentObject: Calculation | PayrollDetails) {

    const navigateToPaymentObject = (businessObjectId) => {
      if (InvoicesLogic.isPayroll(paymentObject)) {
        this.$location.path("/payroll/details/" + businessObjectId + "&refresh=" + new Date().getTime());
      } else {
        this.$location.path("/calc/details/" + businessObjectId + "&refresh=" + new Date().getTime());
      }
    };

    const getPaytrailUrl = (businessObjectId) => {
      if (InvoicesLogic.isPayroll(paymentObject)) {
        return this.ajax.getServerAddress() + `/SalaryPayment/PayInvoiceForPayroll/${businessObjectId}?access_token=${this.ajax.getCurrentToken()}`;
      } else {
        return this.ajax.getServerAddress() + `/SalaryPayment/PayInvoiceForCalculation/${businessObjectId}?access_token=${this.ajax.getCurrentToken()}`;
      }
    };

    const paytrailUrl = getPaytrailUrl(paymentObject.id);
    const resolver = (ev: {
      /** The object passed from the other window. */
      data: ExternalDialogData<any>,
      /** The origin of the window that sent the message at the time postMessage was called. */
      origin: string,
      /** A reference to the window object that sent the message */
      source: MessageEventSource | null,
    }) => {
      if (ev?.data.action == "refresh") {
        this.$timeout(() => {
          navigateToPaymentObject(paymentObject.id);
        });
        return false;
      }
      return true;
    };

    this.uiHelpers.showExternalDialog("PaytrailPayment", paytrailUrl, {}, null, resolver).then((result) => {
        if (result.action == "user-cancel") {
          this.uiHelpers.showDialog("salaxy-components/modals/payment/PaymentCancel.html").then(() => {
            navigateToPaymentObject(paymentObject.id);
          });
        } else if (result.action == "invoice-paid") {
          this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.paytrail.invoicePaid.heading"), Translations.get("SALAXY.NG1.InvoicesService.paytrail.invoicePaid.text") + ".").then(() => {
            navigateToPaymentObject(paymentObject.id);
          });
        } else if (result.action == "invoice-missing") {
          this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.paytrail.invoiceMissing.heading"), Translations.get("SALAXY.NG1.InvoicesService.paytrail.invoiceMissing.text") + ".").then(() => {
          navigateToPaymentObject(paymentObject.id);
          });
        } else if (result.action == EditDialogKnownActions.Ok) {
          this.uiHelpers.showDialog("salaxy-components/modals/payment/PaymentConfirm.html").then(() => {
            navigateToPaymentObject(paymentObject.id);
          });
        }
    });
  }

  private createPalkkausPersonalInvoice(channel: PaymentChannel, paymentObject: Calculation | PayrollDetails, status: InvoiceStatus) {
    if (InvoicesLogic.isPayroll(paymentObject)) {
      this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.palkkausPersonalInvoice.isPayroll.heading"), Translations.get("SALAXY.NG1.InvoicesService.palkkausPersonalInvoice.isPayroll.text") + ".");
      return;
    }

    const updateInvoiceStatus = (invoice: Invoice, newStatus: InvoiceStatus): Promise<Invoice> => {
      if (invoice.header.status === newStatus) {
        return Promise.resolve(invoice);
      }
      return this.invoicesApi.requestStatusUpdate(invoice.id, newStatus);
    };

    const previewInvoices = (): Promise<InvoicePreview[]> => {
      return this.invoicesApi.previewInvoices(channel, {
        calcs: [paymentObject],
      });
    };

    const createNewInvoice = (): Promise<Invoice[]> => {
      return this.invoicesApi.createInvoices(paymentObject, channel);
    };

    const createInvoice = (): Promise<{netInvoice: Invoice, taxInvoice: Invoice}> => {
      const netAndTaxInvoices: {netInvoice: Invoice, taxInvoice: Invoice} = {netInvoice: null, taxInvoice: null};
      return previewInvoices().then((previews) => {
        const netInvoicePreview = previews.find((x) => x.invoice.entityType == InvoiceType.Net && x.invoice.status !== InvoiceStatus.Canceled && x.invoice.status !== InvoiceStatus.Error);
        if (!netInvoicePreview) {
          return Promise.resolve(netAndTaxInvoices);
        }
        const taxInvoicePreview = previews.find((x) => x.invoice.entityType == InvoiceType.Tax && x.invoice.status !== InvoiceStatus.Canceled && x.invoice.status !== InvoiceStatus.Error);

        const getNetInvoice = (): Promise<void> => {
          return this.invoicesApi.getSingle(netInvoicePreview.invoice.id).then((netInvoice) => {
            netAndTaxInvoices.netInvoice = netInvoice;
          });
        };

        const getTaxInvoice = (): Promise<void> => {
          if (!taxInvoicePreview || !taxInvoicePreview.exists) {
            return Promise.resolve(null);
          }
          return this.invoicesApi.getSingle(taxInvoicePreview.invoice.id).then((taxInvoice) => {
            netAndTaxInvoices.taxInvoice = taxInvoice;
          });
        };

        if (netInvoicePreview.exists) {
          return Promise.all([getNetInvoice(), getTaxInvoice()]).then(() => {
            return netAndTaxInvoices;
          });
        }

        return createNewInvoice().then((invoices) => {
          netAndTaxInvoices.netInvoice = invoices.find((x) => x.header.type == InvoiceType.Net);
          netAndTaxInvoices.taxInvoice = invoices.find((x) => x.header.type == InvoiceType.Tax);
          return netAndTaxInvoices;
        });
      });
    };

    const navigateToPaymentObject = (businessObjectId) => {
      this.$location.path("/calc/details/" + businessObjectId + "&refresh=" + new Date().getTime());
    };

    const loader = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.InvoicesService.palkkausPersonalInvoice.updatingPaymentStatus") + "...");
    createInvoice().then((invoices) => {
      if (!invoices.netInvoice) {
        loader.dismiss();
        this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.InvoicesService.palkkausPersonalInvoice.createInvoice.heading"), Translations.get("SALAXY.NG1.InvoicesService.palkkausPersonalInvoice.createInvoice.text") + "...");
        return;
      }

      // If status is Canceled, update only net invoice, backend will handle other invoices cancellation
      if (status === InvoiceStatus.Canceled) {
        updateInvoiceStatus(invoices.netInvoice, status).then(() => {
          loader.dismiss();
          navigateToPaymentObject(invoices.netInvoice.header.businessObjects[0]);
        });
      }

      // if status is not canceled, update net and tax invoices
      if (status !== InvoiceStatus.Canceled) {
        const promises = [
          updateInvoiceStatus(invoices.netInvoice, status)
        ];

        if (invoices.taxInvoice) {
          promises.push(updateInvoiceStatus(invoices.taxInvoice, status));
        }

        Promise.all(promises).then(() => {
          loader.dismiss();
          navigateToPaymentObject(invoices.netInvoice.header.businessObjects[0]);
        });
      }
    });
  }

  private textToRows(text: string, rowLength: number, separator: string): string {
    if (!text) {
      return text;
    }
    const result = [];
    const rows = text.split(separator);
    for (const row of rows) {
      let remaining = row;
      while (remaining.length > rowLength) {
        result.push(remaining.substr(0, rowLength));
        remaining = remaining.slice(rowLength);
      }
      if (remaining.length > 0) {
        result.push(remaining);
      }
    }
    return result.join(separator);
  }
}